import React, { useEffect, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';

import { cssMap, cx } from '@atlaskit/css';
import DropdownMenu from '@atlaskit/dropdown-menu/dropdown-menu';
import DropdownItem from '@atlaskit/dropdown-menu/dropdown-menu-item';
import DropdownItemGroup from '@atlaskit/dropdown-menu/dropdown-menu-item-group';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import PaintBucketIcon from '@atlaskit/icon/core/paint-bucket';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import { TabColorPicker } from './TabColorPicker.tsx';
import { getTabColorAppearance, type TabColorKey } from './tabColorDefinitions.ts';
import { isStandardTabColorPaletteEnabled } from './tabColorFeatureGate.ts';
import { getTabColorPalette } from './tabColorPalettes.ts';

const i18n = defineMessages({
	tabColor: {
		id: 'native-tabs.tab-pill-menu.tab-color.non-final',
		defaultMessage: 'Tab color',
		description: 'Dropdown menu item label to open the color picker for the tab.',
	},
});

// The style prop assigns runtime values to these variables; cssMap consumes them below.
type TabColorPreviewCustomProperties = React.CSSProperties & {
	'--tab-color-preview-background-color'?: string;
};

const styles = cssMap({
	colorPreviewSwatch: {
		width: '16px',
		height: '16px',
		borderRadius: token('radius.small'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		flexShrink: 0,
	},
	colorPreviewSwatchNoColor: {
		backgroundColor: token('elevation.surface'),
	},
	colorPreviewSwatchFromCatalog: {
		// @ts-expect-error -- cssMap does not support runtime CSS variables for colors.
		backgroundColor: 'var(--tab-color-preview-background-color)',
	},
});

export type TabColorMenuItemProps = {
	applyToAll?: boolean;
	currentColor?: TabColorKey;
	isParentMenuOpen: boolean;
	onApplyToAllChange?: (value: boolean) => void;
	onColorChange?: (color: TabColorKey | undefined) => void;
};

/**
 * Owns the complete tab-color section within the tab options menu.
 */
export const TabColorMenuItem: {
	(props: TabColorMenuItemProps): React.JSX.Element;
	displayName: string;
} = ({
	applyToAll,
	currentColor,
	isParentMenuOpen,
	onApplyToAllChange,
	onColorChange,
}: TabColorMenuItemProps): React.JSX.Element => {
	const [isColorMenuOpen, setIsColorMenuOpen] = useState(false);
	const { formatMessage } = useIntl();
	const palette = getTabColorPalette(isStandardTabColorPaletteEnabled());
	const currentColorAppearance = currentColor
		? getTabColorAppearance(currentColor, palette.appearanceMode)
		: undefined;

	useEffect(() => {
		if (!isParentMenuOpen) {
			setIsColorMenuOpen(false);
		}
	}, [isParentMenuOpen]);

	return (
		<DropdownItemGroup>
			<DropdownMenu
				isOpen={isColorMenuOpen}
				onOpenChange={({ isOpen }) => {
					setIsColorMenuOpen(isOpen);
				}}
				placement="right-start"
				trigger={({ triggerRef, ...triggerProps }) => (
					<DropdownItem
						{...triggerProps}
						ref={triggerRef}
						elemBefore={<PaintBucketIcon label={formatMessage(i18n.tabColor)} size="small" />}
						elemAfter={
							<Inline alignBlock="center" space="space.075">
								<Box
									testId="native-tabs-color-swatch-preview"
									style={
										{
											'--tab-color-preview-background-color':
												currentColorAppearance?.swatch.backgroundColor,
										} as TabColorPreviewCustomProperties
									}
									xcss={cx(
										styles.colorPreviewSwatch,
										currentColorAppearance
											? styles.colorPreviewSwatchFromCatalog
											: styles.colorPreviewSwatchNoColor,
									)}
								/>
								<ChevronRightIcon label="" color="currentColor" />
							</Inline>
						}
					>
						<Text color="color.text.subtle">
							<FormattedMessage {...i18n.tabColor} />
						</Text>
					</DropdownItem>
				)}
			>
				<TabColorPicker
					currentColor={currentColor}
					onColorSelect={(colorKey) => {
						onColorChange?.(colorKey ?? undefined);
					}}
					palette={palette}
					applyToAll={applyToAll}
					onApplyToAllChange={(isChecked) => {
						onApplyToAllChange?.(isChecked);
						if (isChecked) {
							onColorChange?.(currentColor);
						}
					}}
				/>
			</DropdownMenu>
		</DropdownItemGroup>
	);
};

TabColorMenuItem.displayName = 'TabColorMenuItem';
